import React, { useEffect, useMemo, useState } from 'react'
import { Box, Button, Dialog, Grid, Tooltip } from '@mui/material'
import { originalIcons } from '@shared/components/icons/icons'
import makeStyles from '@mui/styles/makeStyles'
import {
  AuditResponse,
  CompanyMetrics,
  CompanyProfile,
  Roles,
} from '@shared/types'
import { ShowConditionally } from '@shared/components/show-conditionally.component'
import { ACL } from '@shared/components/acl.component'
import { ProgressButton } from '@shared/components/progress-button.component'
import { useAuditMutation } from '@modules/requirements/use-audit-action.hook'
import { ConfirmationDialog } from '@shared/components/confirmation.dialog.component'
import { useRouter } from '@shared/hooks/use-router'
import { ProgressDownloadButton } from '@shared/components/ProgressDownloadButton'
import { ClientAuditStats } from '@modules/requirements/client-audit-stats.component'
import { ROLES_WITHOUT_CLIENT_BASIC } from '@shared/consts'
import { AuditHeaderTitle } from '@modules/requirements/audit-header-title.component'
import { UnstyledLink } from '@shared/components/unstyled-link.component'
import { TooltipText } from './audit-tooltip-text.component'
import { useQuery } from 'react-query'
import { apiJSON } from '@shared/utils/api/api.util'
import IDS from '@shared/onboarding/ids'

type Props = {
  companyProfile?: CompanyProfile
  audit?: AuditResponse
  isPreviewMode: boolean
  draftsExists: boolean
  currentScopeID: number
  currentTabSelected: string
  isSaveEnabled: boolean
  currentTab?: { id: number; isAudit: boolean }
}

const LABELS = {
  START_DATE: 'Rozpoczęcie',
  END_DATE: 'Zakończenie',
  LEGAL_DATE: 'Stan prawny',
  LOCALIZATION: 'Lokalizacja',
  AUDITORS: 'Audytorzy',
  SAVE_AUDIT: 'Zapisz audyt',
  CHANGE_RANGE: 'Modyfikuj zakresy',
  GENERATE_EXCEL: 'Generuj excel',
  HISTORY: 'Historia audytów',
  CHART: 'Wykres',
  ASPECTS: 'Aspekty',
  DECISIONS: 'Decyzje',
  DOCUMENTS: 'Dokumenty',
  NEWSLETTERS: 'Nowości prawne',
  BRIEFCASE: 'Dokumenty udostępnione',
  CONTINUE_AUDIT: 'Kontynuuj audyt',
  RUN_AUDIT: 'Uruchom audyt',
}

export const AuditHeader = (props: Props) => {
  const {
    companyProfile,
    currentScopeID,
    currentTabSelected,
    audit,
    isPreviewMode,
    currentTab,
  } = props
  const [areStatsOpen, setAreStatsOpen] = useState(false)
  const [currentMetricData, setCurrentMetricData] = useState<
    CompanyMetrics[number] | undefined
  >(undefined)

  const { query } = useRouter<{
    auditID: string
    companyID: number
  }>()
  const { companyID } = query
  const auditID = isPreviewMode
    ? companyProfile?.ongoingAuditId?.toString()
    : audit?.auditId?.toString()
  const classes = useStyles()

  const {
    isAuditMutationInProgress: isAuditRemoving,
    isConfirmAuditMutationDialogOpen: isConfirmRemoveAuditDialogOpen,
    onAuditMutationStart: onAuditRemove,
    onAuditActionConfirmation: onAuditRemoveConfirmation,
  } = useAuditMutation(
    isPreviewMode && !auditID
      ? `CompaniesEvaluationProfiles/${companyID}`
      : `companies/${companyID}/audits/${auditID}`,
    'DELETE'
  )

  const {
    isAuditMutationInProgress: isAuditClosing,
    isConfirmAuditMutationDialogOpen: isConfirmCloseAuditDialogOpen,
    onAuditMutationStart: onAuditClose,
    onAuditActionConfirmation: onAuditCloseConfirmation,
  } = useAuditMutation(
    `companies/${companyID}/audits/${auditID}/publish`,
    'POST',
    {}
  )

  const { data: currentAuditData } = useQuery<{
    localization: string
    auditors: string
  }>(
    `companies/${companyID}/audits/${auditID}`,
    () => apiJSON(`companies/${companyID}/audits/${auditID}`),
    {
      enabled: !!auditID && !isPreviewMode,
    }
  )

  const { data: companyMetrics, isFetching: isCompanyMetricsLoading } =
    useQuery<CompanyMetrics>(
      `CompaniesEvaluationProfiles/metrics?companyId=${companyID}`,
      () =>
        apiJSON(`CompaniesEvaluationProfiles/metrics?companyId=${companyID}`),
      {
        enabled: isPreviewMode,
      }
    )

  const localizationText = isPreviewMode
    ? currentMetricData?.auditMetrics.localization || '-'
    : currentAuditData?.localization || '-'

  const auditorsText = isPreviewMode
    ? currentMetricData?.auditMetrics.auditors || '-'
    : currentAuditData?.auditors || '-'

  useEffect(() => {
    if (!isCompanyMetricsLoading) {
      setCurrentMetricData(
        companyMetrics?.find((scope) => scope.scopeId === currentScopeID)
      )
    }
  }, [currentScopeID, isCompanyMetricsLoading])

  return (
    <>
      <Box className={classes.container}>
        <Box display="flex" flexDirection="row" justifyContent="flex-start">
          <Grid container direction="column" className={classes.detailsSection}>
            <AuditHeaderTitle {...props} />
            {(currentAuditData || companyMetrics) && currentTab?.isAudit ? (
              <ACL allowedRoles={ROLES_WITHOUT_CLIENT_BASIC}>
                <Grid item>
                  <Box display="flex">
                    <TooltipText
                      icon={<originalIcons.Pin className={classes.icon} />}
                      text={LABELS.LOCALIZATION}
                      tooltipText={localizationText}
                    />
                    <TooltipText
                      className={classes.auditors}
                      icon={<originalIcons.Group className={classes.icon} />}
                      text={LABELS.AUDITORS}
                      tooltipText={auditorsText}
                    />
                  </Box>
                </Grid>
              </ACL>
            ) : (
              <Box className={classes.blankBox} />
            )}
          </Grid>
          <Box display="flex" className={classes.mainButtonsWrapper}>
            <Box display="flex" sx={{ columnGap: '10px' }}>
              <ACL allowedRoles={[Roles.Admin]}>
                <ProgressButton
                  isLoading={isAuditRemoving}
                  onClick={onAuditRemove}
                  variant="contained"
                  color="error"
                >
                  <originalIcons.Delete />
                </ProgressButton>
              </ACL>
              <ACL allowedRoles={[Roles.Admin, Roles.Auditor]}>
                <UnstyledLink
                  to={`${isPreviewMode ? `/client/${companyID}` : `/company-profile/${companyID}/${auditID}/run`}`}
                >
                  <Button variant="outlined" startIcon={<originalIcons.Tune />}>
                    {LABELS.CHANGE_RANGE}
                  </Button>
                </UnstyledLink>
              </ACL>
            </Box>
            <ACL
              allowedRoles={[
                Roles.Admin,
                Roles.Auditor,
                Roles.Client,
                Roles.ClientEditor,
                Roles.ClientBasic,
                Roles.ClientAdministrator,
              ]}
            >
              <UnstyledLink to={`/files`}>
                <Button
                  variant="outlined"
                  startIcon={<originalIcons.Work />}
                  id={IDS.BRIEFCASE}
                >
                  {LABELS.BRIEFCASE}
                </Button>
              </UnstyledLink>
            </ACL>
            <ACL allowedRoles={[Roles.Admin, Roles.Auditor]}>
              <ShowConditionally when={isPreviewMode && auditID}>
                <a
                  href={`/company-profile/${companyID}/${auditID}`}
                  style={{ textDecoration: 'none' }}
                >
                  <Button variant="contained" color="primary">
                    {LABELS.CONTINUE_AUDIT}
                  </Button>
                </a>
              </ShowConditionally>
            </ACL>
            <ShowConditionally when={isPreviewMode && !auditID}>
              <ACL allowedRoles={[Roles.Admin, Roles.Auditor]}>
                <UnstyledLink to={`/company-profile/${companyID}/run`}>
                  <Button variant="contained" color="primary">
                    {LABELS.RUN_AUDIT}
                  </Button>
                </UnstyledLink>
              </ACL>
            </ShowConditionally>
            <ShowConditionally when={!isPreviewMode && audit}>
              <ACL allowedRoles={[Roles.Admin, Roles.Auditor]}>
                <Tooltip title="Zapisz wymagania, aby zapisać audyt">
                  <span>
                    <ProgressButton
                      isLoading={isAuditClosing}
                      onClick={onAuditClose}
                      color="primary"
                      variant="contained"
                      disabled={!audit?.isReadyToPublish}
                    >
                      {LABELS.SAVE_AUDIT}
                    </ProgressButton>
                  </span>
                </Tooltip>
              </ACL>
            </ShowConditionally>
          </Box>
        </Box>
        <Box
          display="flex"
          justifyContent="space-between"
          className={classes.buttonsWrapper}
        >
          <ShowConditionally when={!isPreviewMode}>
            <ACL allowedRoles={[Roles.Admin, Roles.Auditor]}>
              <Box display="flex" gap={1}>
                <ProgressDownloadButton
                  variant="outlined"
                  downloadUrl={`${process.env.REACT_APP_HOST}/api/companies/${companyID}/audits/${auditID}/excel`}
                  startIcon={<originalIcons.Send />}
                >
                  {LABELS.GENERATE_EXCEL}
                </ProgressDownloadButton>
              </Box>
            </ACL>
          </ShowConditionally>
          <ACL allowedRoles={ROLES_WITHOUT_CLIENT_BASIC}>
            <ShowConditionally when={isPreviewMode && currentTab?.isAudit}>
              <Button
                variant="outlined"
                startIcon={<originalIcons.Chart />}
                onClick={() => setAreStatsOpen(true)}
                id={IDS.CHART}
              >
                {LABELS.CHART}
              </Button>
            </ShowConditionally>
          </ACL>
          <div />
          <Box display="flex" gap={1}>
            <UnstyledLink to={`/decisions/${companyID}`}>
              <Button
                variant="outlined"
                startIcon={<originalIcons.Gavel />}
                id={IDS.DECISIONS}
              >
                {LABELS.DECISIONS}
              </Button>
            </UnstyledLink>
            <UnstyledLink to={`/aspects/${companyID}`}>
              <Button
                variant="outlined"
                startIcon={<originalIcons.Forest />}
                id={IDS.ASPECTS}
              >
                {LABELS.ASPECTS}
              </Button>
            </UnstyledLink>
            <UnstyledLink to={`/documents/${companyID}`}>
              <Button
                variant="outlined"
                startIcon={<originalIcons.Paper />}
                id={IDS.DOCUMENTS}
              >
                {LABELS.DOCUMENTS}
              </Button>
            </UnstyledLink>
            <ShowConditionally when={isPreviewMode}>
              <UnstyledLink to={`/audit/${companyID}/newsletters`}>
                <Button
                  variant="outlined"
                  startIcon={<originalIcons.Mail />}
                  id={IDS.NEWSLETTERS}
                >
                  {LABELS.NEWSLETTERS}
                </Button>
              </UnstyledLink>
            </ShowConditionally>
            <ACL allowedRoles={ROLES_WITHOUT_CLIENT_BASIC}>
              <ShowConditionally when={currentTab?.isAudit}>
                <UnstyledLink to={`/history/${companyID}`}>
                  <Button
                    variant="outlined"
                    startIcon={<originalIcons.Watch />}
                    id={IDS.HISTORY}
                  >
                    {LABELS.HISTORY}
                  </Button>
                </UnstyledLink>
              </ShowConditionally>
            </ACL>
          </Box>
        </Box>
      </Box>
      {isPreviewMode && companyProfile?.ongoingAuditId !== null && (
        <ConfirmationDialog
          submitButtonProps={{
            children: null,
          }}
          title={'Resetowanie lokalizacji'}
          open={isConfirmRemoveAuditDialogOpen}
          onClose={onAuditRemoveConfirmation}
        >
          Aktualnie trwa audyt. Nie można teraz zresetować lokalizacji.
        </ConfirmationDialog>
      )}
      {isPreviewMode && companyProfile?.ongoingAuditId === null && (
        <ConfirmationDialog
          submitButtonProps={{
            children: 'Usuń',
            isLoading: isAuditRemoving,
          }}
          title={'Resetowanie lokalizacji'}
          open={isConfirmRemoveAuditDialogOpen}
          onClose={onAuditRemoveConfirmation}
        >
          Czy na pewno chcesz zresetować tę lokalizację wraz ze wszystkimi
          opublikowanymi zakresami? Tej operacji nie można cofnąć.
        </ConfirmationDialog>
      )}
      {!isPreviewMode && (
        <ConfirmationDialog
          submitButtonProps={{
            children: 'Resetuj lokalizację',
            isLoading: isAuditRemoving,
          }}
          title={'Czy jesteś pewien, że chcesz usunąć audyt?'}
          open={isConfirmRemoveAuditDialogOpen}
          onClose={onAuditRemoveConfirmation}
        >
          Po usunięciu audyt wraz z informacjami o statusie wymagań zostanie
          permanentnie usunięte.
        </ConfirmationDialog>
      )}
      <ConfirmationDialog
        submitButtonProps={{ children: 'Zakończ' }}
        title="Czy jesteś pewien, że chcesz zakończyć audyt?"
        open={isConfirmCloseAuditDialogOpen}
        onClose={onAuditCloseConfirmation}
      >
        Po zakończeniu, Raport z Audytu wraz z informacjami o statusie wymagań
        zostaną automatycznie udostępnione Klientowi. Upewnij się, że wsystkie
        wymagania zostały sprawdzone oraz, że podsumowanie raportu zostało
        uzupełnione.
      </ConfirmationDialog>
      <ACL
        allowedRoles={[
          Roles.Client,
          Roles.ClientEditor,
          Roles.ClientAdministrator,
          ...(isPreviewMode ? [Roles.Admin, Roles.Auditor] : []),
        ]}
      >
        <Dialog fullWidth open={areStatsOpen} maxWidth="sm">
          <ClientAuditStats
            handleClose={() => setAreStatsOpen(false)}
            isPreviewMode={isPreviewMode}
            companyID={companyID}
            scopeID={currentScopeID}
            currentTabSelected={currentTabSelected}
          />
        </Dialog>
      </ACL>
    </>
  )
}

export const useStyles = makeStyles((theme) => {
  return {
    container: {
      marginBottom: theme.spacing(2),
    },
    detailsSection: {
      marginBottom: theme.spacing(2),
    },
    auditors: {
      marginLeft: theme.spacing(2),
    },
    icon: {
      color: theme.palette.text.secondary,
      marginRight: theme.spacing(0.25),
      fontSize: theme.spacing(2),
    },
    infoText: {
      fontSize: theme.spacing(1.5),
    },
    separator: {
      color: theme.palette.text.secondary,
      fontWeight: 'bold',
      marginLeft: theme.spacing(0.5),
      marginRight: theme.spacing(0.5),
    },
    mainButtonsWrapper: {
      gap: theme.spacing(1),
      alignItems: 'start',
      justifyContent: 'flex-end',
      minWidth: 650,
    },
    buttonsWrapper: {
      backgroundColor: theme.palette.grey['100'],
      borderRadius: theme.spacing(1),
      padding: theme.spacing(2),
    },
    blankBox: {
      height: '17px',
    },
  }
})
